import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { School, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import logo from '../assets/LuminaAIFULL.png';

export default function LandingPage() {
  const [schoolCode, setSchoolCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { checkSchoolCode, setInternalSchool } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolCode.trim()) {
      toast.error('Please enter a school code');
      return;
    }

    setLoading(true);
    try {
      await checkSchoolCode(schoolCode.trim());
      toast.success('School found! Please login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'School not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-slate-50 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Branding */}
        <div className="space-y-8">
          <div className="flex items-center gap-4">
            <div className="w-28 h-28 flex items-center justify-center rounded-3xl overflow-hidden shadow-md border border-slate-200/60 bg-white">
              <img src={logo} alt="SchoolBird Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-outfit font-bold text-4xl text-slate-800">Lumina AI Campus</h1>
              <p className="text-sm text-slate-500">Smart School Management System</p>
            </div>
          </div>

          <div>
            <h2 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-800 mb-4">
              Manage Your School,<br />
              <span className="text-indigo-600">Effortlessly.</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              A modern, intuitive platform designed to streamline school operations,
              from attendance tracking to fee management.
            </p>
          </div>

          <div className="space-y-3">
            {['Student & Teacher Management', 'Attendance & Exam Tracking', 'Fee Management & Reports'].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Login Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8 lg:p-10">
          <div className="mb-8">
            <h3 className="font-outfit font-semibold text-2xl text-slate-800 mb-2">
              Welcome Back
            </h3>
            <p className="text-slate-500">
              Enter your school code to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                School Code
              </label>
              <input
                data-testid="school-code-input"
                type="text"
                value={schoolCode}
                onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                placeholder="Enter your school code"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              />
            </div>

            <button
              data-testid="continue-button"
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-full flex items-center justify-center gap-2 shadow-sm active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <p className="text-sm text-indigo-700 font-medium mb-1">Demo Access</p>
              <p className="text-xs text-indigo-600">School Code: <span className="font-semibold">DEMO2024</span></p>
            </div>
          )}

          <div className="mt-8 text-center">
            <button 
              onClick={() => {
                setInternalSchool();
                navigate('/login');
              }}
              className="text-sm text-slate-400 hover:text-indigo-600 transition-colors font-medium border-b border-transparent hover:border-indigo-200 pb-0.5"
            >
              Lumina AI Campus Internal Team? Login here
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
